import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { OnboardingSidebar } from './OnboardingSidebar';
import { OnboardingMobileStrip } from './OnboardingMobileStrip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';
import { useUserConfigState } from '@/context/UserConfigContext';
import { useOnboardingWizard } from '@/context/OnboardingWizardContext';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { focusField } from '@/utils/focus';
import { AnimatePresence, motion } from 'framer-motion';
import { ValidationProvider, ValidationContext, ValidationFooter, mergeSchemas } from '@/components/validation';
import type { SchemaEntry } from '@/components/validation';
import {
  profileSchema,
  rolesSchema,
  premiumShiftSchema,
  multipliersSchema,
  billingSchema,
  mealBreakSchema,
  shortTurnSchema,
  holidaysSchema,
  overnightSchema,
  travelSchema,
  expenseSchema,
  invoiceSchema,
} from '@/utils/validation/schemas';

interface OnboardingLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  steps: string[];
  showStepper?: boolean;
  showNavigation?: boolean;
  nextButtonText?: string;
  previousButtonText?: string;
  onNext?: () => boolean | Promise<boolean>;
  onBack?: () => void;
  hideNextButton?: boolean;
  hideBackButton?: boolean;
  isLastStep?: boolean;
  /** Kept during transition for imperative-handle validity (unsaved inline edits).
   *  Schema-level validation is handled by ValidationProvider. */
  externalStepValid?: boolean;
  /** Hint text shown inline with navigation buttons (e.g. "optional step" notice) */
  navigationHint?: React.ReactNode;
}

/** Inner component that consumes ValidationContext. */
const OnboardingLayoutInner: React.FC<OnboardingLayoutProps> = ({
  children,
  title,
  subtitle,
  steps,
  showStepper = true,
  showNavigation = true,
  nextButtonText = "Next",
  previousButtonText = "Back",
  onNext,
  onBack,
  hideNextButton = false,
  hideBackButton = false,
  isLastStep = false,
  externalStepValid = true,
  navigationHint,
}) => {
  const ctx = useContext(ValidationContext);
  if (!ctx) throw new Error('OnboardingLayoutInner must be used within ValidationProvider');

  const {
    currentStep,
    isSubmitting,
    goToNextStep,
    goToPreviousStep,
    markStepAsCompleted,
    submitOnboarding
  } = useOnboardingWizard();

  const stepScope = String(currentStep);
  // Combine schema validation with any imperative-handle validity signals
  // (e.g. unsaved inline edits in roles/multipliers forms)
  const stepValid = ctx.isValid(stepScope) && externalStepValid;

  const [isAnimating, setIsAnimating] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);

  // Track whether the user has attempted to advance so footer hint only appears after
  const [hasAttemptedAdvance, setHasAttemptedAdvance] = useState(false);

  // Reset attempted-advance flag when step changes
  useEffect(() => {
    setHasAttemptedAdvance(false);
  }, [currentStep]);

  const wizardStateRef = useRef({ currentStep, goToPreviousStep });
  wizardStateRef.current = { currentStep, goToPreviousStep };

  useEffect(() => {
    const handlePopState = () => {
      if (wizardStateRef.current.currentStep > 0) {
        wizardStateRef.current.goToPreviousStep();
        toast.dismiss();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);


  const handleNext = async () => {
    if (isAnimating || isNextLoading) return;
    if (!stepValid) return;

    setIsNextLoading(true);
    try {
      if (onNext) {
        const canProceed = await onNext();
        if (!canProceed) {
          setIsNextLoading(false);
          return;
        }
      }
      markStepAsCompleted(currentStep);
      if (isLastStep) {
        await submitOnboarding();
      } else {
        toast.dismiss();
        setIsAnimating(true);
        window.history.pushState({ step: currentStep }, '');
        setTimeout(() => {
          goToNextStep();
          setIsAnimating(false);
        }, 300);
      }
    } finally {
      setIsNextLoading(false);
    }
  };

  const handleBack = async () => {
    if (isAnimating) return;
    if (onBack) onBack();
    toast.dismiss();
    setIsAnimating(true);
    setTimeout(() => {
      goToPreviousStep();
      setIsAnimating(false);
    }, 300);
  };

  const hasSchemaErrors = ctx.getActiveErrors(stepScope).length > 0;
  const showFooterHint = hasSchemaErrors && !isAnimating && hasAttemptedAdvance;

  const navigationBar = showNavigation && (
    <div className="w-full">
      <div className="flex items-center justify-between">
        {currentStep > 0 && (
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={isAnimating || hideBackButton || isSubmitting}
            className={cn(
              "flex items-center gap-2 justify-center min-w-[80px] text-sm px-4 py-3 md:min-w-[100px] md:py-2 shrink-0",
              "bg-surface-2 border-border text-foreground",
            )}
            data-testid="back-button"
            aria-label={previousButtonText}
          >
            <ArrowLeft aria-hidden="true" className="w-4 h-4" />
            {previousButtonText}
          </Button>
        )}

        <div className="flex-1 px-3 min-w-0">
          {showFooterHint ? (
            <ValidationFooter scope={stepScope} />
          ) : navigationHint ? (
            <p className="text-[11px] text-muted-foreground text-center">
              {navigationHint}
            </p>
          ) : null}
        </div>

        {!hideNextButton && (
            <Button
              onClick={() => {
                if (!stepValid) {
                  setHasAttemptedAdvance(true);
                  ctx.triggerShake();
                  const errors = ctx.getActiveErrors(stepScope);
                  if (errors.length > 0) {
                    setTimeout(() => focusField(errors[0].field), 100);
                  }
                  return;
                }
                handleNext();
              }}
              disabled={isAnimating || isNextLoading || isSubmitting}
              aria-disabled={!stepValid || undefined}
              className={cn(
                "flex items-center gap-2 justify-center shrink-0",
                "min-w-[80px] text-sm px-4 py-3 md:min-w-[100px] md:py-2",
                "bg-primary text-surface-0 hover:bg-primary-hover",
                "active:translate-y-[1px]",
                !stepValid && "opacity-50 dark:opacity-30 cursor-not-allowed",
              )}
              data-testid="next-button"
              aria-label={nextButtonText}
            >
              {isLastStep ? (
                isSubmitting ? "Submitting..." : "Complete"
              ) : (
                <>
                  {isNextLoading ? "Checking..." : nextButtonText}
                  <ArrowRight aria-hidden="true" className="w-4 h-4" />
                </>
              )}
            </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-surface-0 onboarding-layout">
      {/* Desktop sidebar */}
      {showStepper && <OnboardingSidebar steps={steps} />}

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-0 overflow-y-auto relative">
        {/* Mobile strip */}
        {showStepper && <OnboardingMobileStrip steps={steps} />}

        {/* Content */}
        <main
          data-testid="onboarding-layout"
          className="w-full max-w-3xl mx-auto px-3 py-4 pb-24 md:px-8 md:py-10 md:pb-10"
        >
          {/* Step title */}
          <div className="mb-6 md:mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-[0.08em] text-foreground">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-2 text-sm md:text-base text-muted-foreground">
                {subtitle}
              </p>
            )}
          </div>

          {/* Animated step content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0, transition: { duration: 0.2, ease: "easeOut" } }}
              exit={{ opacity: 0, x: -20, transition: { duration: 0.15, ease: "easeIn" } }}
              className="w-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>

          {/* Desktop navigation (inline) */}
          <div className="hidden md:block mt-8">
            {navigationBar}
          </div>
        </main>

        {/* Mobile sticky footer */}
        <div
          className={cn(
            "sticky bottom-0 md:hidden z-50",
            "bg-surface-0 border-t border-border",
            "px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]",
          )}
        >
          {navigationBar}
        </div>
      </div>
    </div>
  );
};

/** Outer component: builds schemaMap from state and wraps in ValidationProvider. */
export const OnboardingLayout: React.FC<OnboardingLayoutProps> = (props) => {
  const state = useUserConfigState();

  const schemaMap: Record<string, SchemaEntry> = useMemo(() => ({
    '0': {
      schema: profileSchema,
      data: state,
    },
    '1': {
      schema: mergeSchemas(rolesSchema, premiumShiftSchema),
      data: state,
    },
    '2': {
      schema: mergeSchemas(
        multipliersSchema,
        billingSchema,
        mealBreakSchema,
        shortTurnSchema,
        holidaysSchema,
        overnightSchema,
      ),
      data: state,
    },
    '3': {
      schema: mergeSchemas(travelSchema, expenseSchema),
      data: state,
    },
    '4': {
      schema: invoiceSchema,
      data: state,
    },
  }), [state]);

  return (
    <ValidationProvider
      showToasts={false}
      showSuccessFeedback={false}
      schemaMap={schemaMap}
    >
      <OnboardingLayoutInner {...props} />
    </ValidationProvider>
  );
};
